import React, { useState, useEffect } from 'react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import Loading from '../../../components/ui/Loading';
import Alert from '../../../components/ui/Alert';
import { Shield, Users, ChevronRight, Save, X, Check } from 'lucide-react';
import { userManagementService } from '../../../services';
import type { Role, Screen, ScreenPermission } from '../../../types';

const RolesPermissionsTab: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<ScreenPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole.role_id);
    }
  }, [selectedRole]);

  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [rolesResponse, screensResponse] = await Promise.all([
        userManagementService.getRoles(),
        userManagementService.getScreens(),
      ]);
      
      setRoles(rolesResponse.roles || []);
      setScreens(screensResponse.screens || []);
      
      // Auto-select first role
      if (rolesResponse.roles && rolesResponse.roles.length > 0) {
        setSelectedRole(rolesResponse.roles[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch roles');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId: number) => {
    try {
      setError(null);
      const response = await userManagementService.getRoleScreenPermissions(roleId);
      setPermissions(response.permissions || []);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
    }
  };

  const handlePermissionChange = (
    screenId: number,
    permissionType: 'can_view' | 'can_create' | 'can_edit' | 'can_delete' | 'can_export',
    value: boolean
  ) => {
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.screen_id === screenId
          ? { ...perm, [permissionType]: value }
          : perm
      )
    );
    setHasChanges(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setIsSaving(true);
      setError(null);
      
      await userManagementService.updateRoleScreenPermissions(selectedRole.role_id, {
        permissions,
      });
      
      setSuccess('Permissions updated successfully');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole.role_id);
    }
  };

  // Group screens by parent (for hierarchical display)
  const groupedScreens = permissions.reduce((acc, perm) => {
    const screen = screens.find((s) => s.screen_id === perm.screen_id);
    if (!screen) return acc;

    const parentId = screen.parent_screen_id || 0;
    if (!acc[parentId]) {
      acc[parentId] = [];
    }
    acc[parentId].push({ ...perm, screen });
    return acc;
  }, {} as Record<number, Array<ScreenPermission & { screen: Screen }>>);

  const renderPermissionRow = (perm: ScreenPermission & { screen: Screen }, isChild = false) => (
    <tr key={perm.screen_id} className={isChild ? 'bg-surface-secondary/50' : ''}>
      <td className="px-4 py-3 text-sm">
        <div className={`flex items-center gap-2 ${isChild ? 'pl-6' : ''}`}>
          {isChild && <ChevronRight size={14} className="text-text-muted" />}
          <span className="font-medium text-text-primary">{perm.screen_name}</span>
          {perm.screen.screen_path && (
            <span className="text-xs text-text-muted">({perm.screen.screen_path})</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={perm.can_view}
          onChange={(e) => handlePermissionChange(perm.screen_id, 'can_view', e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
        />
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={perm.can_create}
          onChange={(e) => handlePermissionChange(perm.screen_id, 'can_create', e.target.checked)}
          disabled={!perm.can_view}
          className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary disabled:opacity-50"
        />
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={perm.can_edit}
          onChange={(e) => handlePermissionChange(perm.screen_id, 'can_edit', e.target.checked)}
          disabled={!perm.can_view}
          className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary disabled:opacity-50"
        />
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={perm.can_delete}
          onChange={(e) => handlePermissionChange(perm.screen_id, 'can_delete', e.target.checked)}
          disabled={!perm.can_view}
          className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary disabled:opacity-50"
        />
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={perm.can_export}
          onChange={(e) => handlePermissionChange(perm.screen_id, 'can_export', e.target.checked)}
          disabled={!perm.can_view}
          className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary disabled:opacity-50"
        />
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(error || success) && (
        <>
          {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
          {success && <Alert variant="success" message={success} onClose={() => setSuccess(null)} />}
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Roles & Permissions</h2>
          <p className="text-text-muted text-sm mt-1">Manage screen access and permissions for each role</p>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleCancelChanges} disabled={isSaving}>
              <X size={18} className="mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSavePermissions} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loading size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Roles List */}
        <div className="col-span-12 lg:col-span-3">
          <Card>
            <div className="p-4 border-b border-border-primary">
              <h3 className="font-semibold text-text-primary flex items-center gap-2">
                <Shield size={18} />
                Roles
              </h3>
            </div>
            <div className="divide-y divide-border-primary">
              {roles.map((role) => (
                <button
                  key={role.role_id}
                  onClick={() => {
                    if (hasChanges) {
                      if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
                        setSelectedRole(role);
                        setHasChanges(false);
                      }
                    } else {
                      setSelectedRole(role);
                    }
                  }}
                  className={`w-full p-4 text-left transition-colors ${
                    selectedRole?.role_id === role.role_id
                      ? 'bg-accent-primary/10 border-l-4 border-accent-primary'
                      : 'hover:bg-surface-secondary'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-text-primary">{role.role_name}</span>
                    {selectedRole?.role_id === role.role_id && (
                      <Check size={16} className="text-accent-primary" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <Users size={12} />
                    <span>{role.user_count || 0} users</span>
                    <span>•</span>
                    <span>Level {role.role_level}</span>
                  </div>
                  {role.is_system_role && (
                    <Badge variant="default" className="mt-2 text-xs">System Role</Badge>
                  )}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Permissions Matrix */}
        <div className="col-span-12 lg:col-span-9">
          {selectedRole ? (
            <Card>
              <div className="p-4 border-b border-border-primary">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-text-primary">
                      {selectedRole.role_name} Permissions
                    </h3>
                    <p className="text-sm text-text-muted mt-1">
                      Configure screen access and permissions for this role
                    </p>
                  </div>
                  <Badge variant={selectedRole.is_active ? 'success' : 'danger'}>
                    {selectedRole.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-primary bg-surface-secondary">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider">
                        Screen
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-text-primary uppercase tracking-wider w-20">
                        View
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-text-primary uppercase tracking-wider w-20">
                        Create
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-text-primary uppercase tracking-wider w-20">
                        Edit
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-text-primary uppercase tracking-wider w-20">
                        Delete
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-text-primary uppercase tracking-wider w-20">
                        Export
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {/* Parent screens */}
                    {groupedScreens[0]?.map((perm) => (
                      <React.Fragment key={perm.screen_id}>
                        {renderPermissionRow(perm, false)}
                        {/* Child screens */}
                        {groupedScreens[perm.screen_id]?.map((childPerm) =>
                          renderPermissionRow(childPerm, true)
                        )}
                      </React.Fragment>
                    ))}
                    {(!groupedScreens[0] || groupedScreens[0].length === 0) && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                          No screens available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
                  <Shield size={32} className="text-text-muted" />
                </div>
                <h3 className="text-lg font-medium text-text-primary mb-2">Select a role</h3>
                <p className="text-text-muted">
                  Choose a role from the list to view and manage its permissions
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolesPermissionsTab;
